// The environment's shared folder on a task group's page (D84): where the owner puts files in the
// VM, where the agents read them (read-only), and what is there now. Shown to the group's owner
// and to whoever may see every group (ViewAllTaskGroups); the VM terminal link only to those who
// may open it (D79). A stopped environment (between tasks) cannot be listed: the card says so.
import { File, FolderSimple, FolderSimpleDashed } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { accessQuery, can } from "../../auth/access";
import { useT } from "../../i18n";
import { CollapsibleCard } from "../../ui/CollapsibleCard";
import { EmptyState, ErrorState, Loading } from "../../ui/states";
import { Chip, SummaryRow } from "../../ui/summary";
import { dayTime } from "../../workloads/format";
import { mayOpenShell } from "../../workloads/environment/shell";
import type { TaskGroup } from "../model";
import { fileSize, isStopped, type SharedEntry, type SharedFiles, sharedFilesQuery } from "./sharedFiles";
import { sharedSummary } from "./summaries";

function Path({ label, path }: { label: string; path: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 text-sm text-gray-600">
      <span>{label}</span>
      <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-900">{path}</code>
    </div>
  );
}

function Entry({ entry, folder }: { entry: SharedEntry; folder: string }) {
  const icon = "h-4 w-4 shrink-0 text-gray-500";
  return (
    <li className="flex items-center gap-2 py-1.5 text-sm">
      {entry.dir ? <FolderSimple weight="duotone" className={icon} aria-label={folder} /> : <File weight="duotone" className={icon} aria-hidden />}
      <span className="min-w-0 flex-1 break-all font-mono text-xs text-gray-900">{entry.path}</span>
      {entry.dir ? null : <span className="tabular shrink-0 text-xs text-gray-500">{fileSize(entry.size)}</span>}
      <span className="tabular hidden shrink-0 text-xs text-gray-500 @[24rem]:inline">{dayTime(entry.modified_ms)}</span>
    </li>
  );
}

function Summary({ data, stopped }: { data: SharedFiles | undefined; stopped: boolean }) {
  const c = useT().tasks.cards.shared;
  if (stopped) {
    return (
      <SummaryRow>
        <Chip>{c.stopped}</Chip>
      </SummaryRow>
    );
  }
  if (!data) return null;
  const s = sharedSummary(data);
  if (s.files === 0) {
    return (
      <SummaryRow>
        <Chip>{c.empty}</Chip>
      </SummaryRow>
    );
  }
  return (
    <SummaryRow>
      <Chip tone="primary">{s.more ? c.more(s.files) : c.files(s.files)}</Chip>
      <Chip priority="optional">{fileSize(s.bytes)}</Chip>
    </SummaryRow>
  );
}

export function SharedFilesCard({ group }: { group: TaskGroup }) {
  const t = useT().tasks.group.shared;
  const me = useQuery(accessQuery()).data;
  const env = group.environment_id;
  const visible = !!env && !!me && (me.id === group.owner_user_id || can(me, "ViewAllTaskGroups"));
  const files = useQuery({ ...sharedFilesQuery(env ?? ""), enabled: visible });
  if (!visible || !env) return null;
  const terminal = mayOpenShell(me, group.org_id, true) ? (
    <Link to={`/environments/${env}/terminal`} className="text-sm font-medium text-primary hover:underline">
      {t.openTerminal}
    </Link>
  ) : null;
  const data = files.data;
  return (
    <CollapsibleCard
      kind="shared-files"
      icon={<FolderSimple weight="duotone" />}
      title={t.title}
      description={t.description}
      summary={<Summary data={data} stopped={files.isError && isStopped(files.error)} />}
    >
      <div className="space-y-1">
        <Path label={t.vmPath} path={data?.vm_path ?? "/workspaces/shared"} />
        <Path label={t.agentPath} path={data?.agent_path ?? "/shared"} />
      </div>
      <div className="mt-4">
        {files.isPending ? <Loading /> : null}
        {files.isError && isStopped(files.error) ? <p className="text-sm text-gray-600">{t.stopped}</p> : null}
        {files.isError && !isStopped(files.error) ? <ErrorState message={files.error.message} /> : null}
        {data && data.entries.length === 0 ? (
          <EmptyState compact icon={<FolderSimpleDashed weight="duotone" />} title={t.empty} action={terminal}>
            {t.emptyBody}
          </EmptyState>
        ) : null}
        {data && data.entries.length > 0 ? (
          <>
            <ul className="@container divide-y divide-gray-100" aria-label={t.title}>
              {data.entries.map((e) => (
                <Entry key={e.path} entry={e} folder={t.folder} />
              ))}
            </ul>
            {data.truncated ? <p className="mt-2 text-xs text-gray-500">{t.truncated}</p> : null}
            {terminal ? <div className="mt-3">{terminal}</div> : null}
          </>
        ) : null}
      </div>
    </CollapsibleCard>
  );
}
